<template>
  <modal
    v-if="show"
    height="500px"
    width="609px"
    bodyOverflow="auto"
    :title="t('Integrate Account')"
    @close="close"
    :read-article-url="integration.guideLink"
  >
    <div class="w100 overflow-auto campaign-details__wrap wrap integration-wrp">
      <img
        class="d-block mb-16 logo"
        :alt="integration.name"
        :src="'/images/integrations/' + integration.img.src"
      />

      <ui-input
        v-model="form.name"
        :label="t('Integration Name') + '*'"
        :placeholder="t('Custom Name')"
        :error-messages="errorsDict['name']"
      />

      <ui-input
        v-if="integration.clientId"
        v-model="form.clientId"
        :label="integration.clientId.label"
        :label-help="integration.clientId.labelHelp"
        :placeholder="integration.clientId.placeholder || integration.clientId.label"
        :error-messages="errorsDict['clientId']"
      />

      <ui-input
        v-model="form.accessToken"
        :disabled="isEdit && !tokenEditMode"
        :class="{
          token__isEdit: isEdit,
          token__tokenEditMode: tokenEditMode,
        }"
        :label="integration.accessToken?.label"
        :label-help="integration.accessToken?.labelHelp"
        :placeholder="integration.accessToken?.placeholder"
        :error-messages="errorsDict['accessToken']"
      >
        <template #append>
          <ui-button
            v-if="isEdit"
            @click="handleEditTokenMode"
            class="token-change-mode"
            :icon="tokenEditMode ? 'fas fa-times' : 'fas fa-pen'"
          />
        </template>
      </ui-input>

      <p v-html="integration.help" />
    </div>

    <template #footer>
      <section
        class="justify-space-between align-center xs12 error-wrapper"
        style="display: flex; width: 100%"
      >
        <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        <div class="d-flex justify-end">
          <ui-update-info infoFormat="short" v-if="form.id" :data="props.data" />
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="loading"
            @click="close"
          />

          <ui-button
            :text="isEdit ? t('Save') : t('Create Integration')"
            icon="fas fa-check"
            button-style="accent"
            :loading="loading"
            @click="save"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import {
  postIntegrationApiRequest,
  putIntegrationApiRequest,
} from '@/services/api/integrations-api';
import { useApiFieldErrors, useI18n, useIntegrations } from '@/composables';
import { $emitter } from '@/services/emitter';

type IntegrationForm = {
  name: string;
  accessToken: string;
  type: string;
  id?: string;
  clientId?: string;
  meta?: {
    eventSourceUrl?: string;
  };
};

const props = defineProps<{
  data?: Partial<IntegrationForm> & { type?: string };
}>();

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const { integrationsData } = useIntegrations();
const { errors, errorsDict, errorsControlActive, clearErrors, applyApiErrors } =
  useApiFieldErrors();

const show = ref(false);
const loading = ref(false);
const isEdit = ref(false);
const tokenEditMode = ref(false);
const savedToken = ref('');

const getDefaultIntegration = (): Integration => ({
  name: '',
  type: '',
  img: { src: '' },
  guideLink: '',
  help: '',
});

const integration = ref<Integration>(getDefaultIntegration());

const getDefaultForm = (): IntegrationForm => ({
  name: '',
  accessToken: '',
  type: '',
  id: '',
});

const form = reactive<IntegrationForm>(getDefaultForm());

const dataToSend = computed(() => {
  const { name, accessToken, id, type, clientId = '', meta } = form;
  return {
    ...(isEdit.value && { id }),
    ...(integration.value.clientId && { clientId }),
    ...(integration.value.type === 'facebook-conversions' && {
      meta: { eventSourceUrl: meta?.eventSourceUrl },
    }),
    ...(((isEdit.value && tokenEditMode.value) || !isEdit.value) && { accessToken }),
    name,
    type,
  };
});

const close = () => {
  emit('close');
};

const save = async () => {
  clearErrors();
  loading.value = true;
  try {
    if (isEdit.value) {
      await putIntegrationApiRequest(dataToSend.value);
    } else {
      await postIntegrationApiRequest(dataToSend.value);
    }
    close();
    $emitter.emit('toast-success', t('Integration has been saved'));
    $emitter.emit('integrations-changed');
  } catch (error: unknown) {
    applyApiErrors(error, { nested: true });
  } finally {
    loading.value = false;
  }
};

const handleEditTokenMode = () => {
  if (tokenEditMode.value) {
    form.accessToken = savedToken.value;
  } else {
    form.accessToken = '';
  }
  tokenEditMode.value = !tokenEditMode.value;
};

onMounted(() => {
  const integrationType = props.data?.type || '';
  integration.value = integrationsData.value[integrationType] || getDefaultIntegration();

  if (integration.value.type === 'facebook-conversions') {
    form.meta = {
      eventSourceUrl: '',
    };
  }

  if (integration.value.clientId) {
    form.clientId = '';
  }

  Object.assign(form, getDefaultForm(), props.data);

  tokenEditMode.value = false;
  if (props.data?.id) {
    isEdit.value = true;
    savedToken.value = props.data.accessToken || '';
  }

  show.value = true;
});
</script>

<style lang="scss" scoped>
.choose-integration-dialog__body {
  display: block;
  background: var(--color-gray5);
}
.list {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  overflow-y: auto;
  padding: 16px 8px 0;
}

.search {
  width: 100%;
  padding: 16px 16px 0;
}
.no-data-found {
  margin: 0 8px;
}

.token {
  &__isEdit {
    :deep(input) {
      padding-right: 30px !important;
    }

    :deep(.v-input__append) {
      margin-inline-start: 8px;
    }

    :deep(.v-input--disabled .v-input__append) {
      pointer-events: auto;
    }
  }
}

.token-change-mode {
  margin: 0;
  background: none !important;
  border: none !important;

  :deep(.ui-button) {
    justify-content: center;
  }

  :deep(.ui-button__main) {
    justify-content: center;
    flex: 0 0 auto;
  }
}

.logo {
  height: 40px;
}

.integration-wrp {
  overflow-x: hidden !important;
}
</style>
